import {Sequence} from 'components/Sequence';
import * as TaskRunner from 'util/TaskRunner';

import {
  ChromosomalCoords,
  DBHandle,
  DBImportSequence,
  DBQueryResponse,
  EnsemblDBImportAccession,
  EnsemblDBImportAccessionData,
  SequenceImportOptions,
} from './ImportSequenceConstants';

export async function queryAccessionsFromDB(
  queryString: string,
  speciesName: string | undefined
): Promise<{accessionInfos: Array<DBQueryResponse>}> {
  return await TaskRunner.postJSONPromise({
    url: '/1/api/dbimport/actions/query',
    data: {query: queryString, species: speciesName},
  });
}

export async function importSequencesFromDB(
  dbImportSequences: Array<DBImportSequence>,
  sequenceImportOptions: SequenceImportOptions
): Promise<{results: Array<Sequence>}> {
  const sequences = dbImportSequences
    .filter(({importOptions}) => importOptions.isSelected)
    .map(({resultData, importOptions}) => {
      const getEnsemblAccession = (): EnsemblDBImportAccessionData => {
        const asEnsemblAccession = importOptions.accession as EnsemblDBImportAccession;
        const selectedVersion = resultData.versions![importOptions.selectedVersionIndex!];
        const {gene} = selectedVersion;
        return {
          downstream: asEnsemblAccession.downstream,
          upstream: asEnsemblAccession.upstream,
          transcripts: asEnsemblAccession.transcriptIds,
          gene,
          chromosomeName: selectedVersion.chromosome.name,
          speciesName: selectedVersion.genome.organismName,
        };
      };

      return {
        folder_id: sequenceImportOptions.folderId,
        dbhandle: resultData.dbHandle,
        name: importOptions.name,
        revcomp: importOptions.revcomp ?? false,
        cdna: importOptions.asCDNA ?? false,
        accession: [DBHandle.ENSEMBL, DBHandle.PRIVATE].includes(resultData.dbHandle)
          ? getEnsemblAccession()
          : importOptions.accession,
      };
    });

  let studyIds: Array<string> = [];
  if (sequenceImportOptions.studySearchItems) {
    studyIds = sequenceImportOptions.studySearchItems.map((studyItem) => studyItem.id);
  }

  return await TaskRunner.postJSONPromise({
    url: '/1/api/dbimport',
    data: {
      sequences,
      nucleotideType: sequenceImportOptions.nucleotideType,
      tagSchemaId: sequenceImportOptions.tagSchemaId,
      studyIds,
    },
  });
}

export async function importChromosomalRegions(
  chromosomeCoords: Array<ChromosomalCoords>,
  sequenceImportOptions: SequenceImportOptions,
  genomeName: string,
  fromLiveEnsembl: boolean
): Promise<{sequences: Array<Sequence>}> {
  let studyIds: Array<string> = [];
  if (sequenceImportOptions.studySearchItems) {
    studyIds = sequenceImportOptions.studySearchItems.map((studyItem) => studyItem.id);
  }
  return await TaskRunner.postJSONPromise({
    url: '/1/api/dbimport/actions/batch-import-sequences',
    data: {
      folderId: sequenceImportOptions.folderId,
      chromosomeCoords,
      genomeName,
      fromLiveEnsembl,
      tagSchemaId: sequenceImportOptions.tagSchemaId,
      studyIds,
    },
  });
}
